import { useMaintenanceCheck, MaintenanceScreen } from '../MaintenanceGate';
import React, { useState, useEffect, useMemo, useTransition, useDeferredValue } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, regionsOf, regionLabel } from '../AuthContext';
// AR only (1A consolidation): a user holding exactly one numbered East Coast region (Yoon / Adid)
// does not also get the plain 'East Coast' bucket (unassigned rows stay with the RSH, who holds both).
const arRegionsOf = (u) => {
  const r = regionsOf(u);
  const nums = r.filter(x => /^East Coast \d$/.test(x));
  return nums.length === 1 ? r.filter(x => x !== 'East Coast') : r;
};
const RSH_EMAILS = ['rosvinder.kaur@ninjavan.co', 'afiq.hadi@ninjavan.co'];
const HEAD_OF_SALES_EMAILS = ['dwayne.tung@ninjavan.co'];
function getUserTitle(email) {
  if (HEAD_OF_SALES_EMAILS.includes(email)) return 'Deputy Head, Ninja Mart';
  return RSH_EMAILS.includes(email) ? 'Regional Sales Head' : 'Regional Sales Supervisor';
}
function fmtNum(n) {
  if (n == null || n === '') return '0.00';
  const num = Number(n);
  if (isNaN(num)) return '0.00';
  return num.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
const NAV_BLUE = '#1565c0';
const LANG = {
  en: {
    pageTitle:     (r) => `Credit & Collection Summary (Region ${r})`,
    logout:        'Sign out',
    activeOutlets: 'Active Outlets',
    totalAR:       'Total AR',
    totalOverdue:  'Total AR Overdue',
    gaugeLbl:      '% AR Overdue',
    safeZone:      'Safe zone <= 30%',
    outletList:    'AR Outlet List',
    refresh:       'Refresh',
    outletId:      'Outlet ID',
    outletName:    'Outlet Name',
    vanId:         'Van ID',
    pending:       'AR (Pending Delivery)',
    predue:        'AR (Pre-due)',
    overdue:       'AR (Overdue)',
    totalCol:      'Total AR',
    loading:       'Loading...',
    noData:        'No AR records',
    error:         'Failed to load AR data.',
    invoicePending:'AR Invoice (Pending Delivery)',
    invoicePredue: 'AR Invoice (Pre-due)',
    invoiceOverdue:'AR Invoice (Overdue)',
    invoiceId:     'Invoice ID',
    amount:        'Amount (RM)',
    comment:       'Remark',
    update:        'Update',
    updateTitle:   'Update Remark',
    cancel:        'Cancel',
    save:          'Save',
    saving:        'Saving...',
    remarkPh:      'Enter remark...',
    navAR:         'AR Summary',
    navCAS:        'Credit Applications',
    navMR:         'Monthly Review',
    filterOutletId:   'Search Outlet ID...',
    filterOutletName: 'Search Outlet Name...',
    filterVanId:      'Search Van ID...',
    filterStatus:     'All Status',
    filterClear:      'Clear',
    statusLabel:      'AR Status',
    regionLabel:      'Region',
    allRegions:       'All Regions',
    siteLabel:        'Site',
    allSites:         'All Sites',
  },
  bm: {
    pageTitle:     (r) => `Ringkasan Kredit & Tunggakan (Region ${r})`,
    logout:        'Log keluar',
    activeOutlets: 'Outlet Aktif',
    totalAR:       'Jumlah AR',
    totalOverdue:  'Jumlah AR Tertunggak',
    gaugeLbl:      'Peratusan AR Tertunggak',
    safeZone:      'Zon Selamat <= 30%',
    outletList:    'Senarai Outlet AR',
    refresh:       'Muat Semula',
    outletId:      'Outlet ID',
    outletName:    'Nama Outlet',
    vanId:         'Van ID',
    pending:       'Invois AR (Proses Penghantaran)',
    predue:        'Invois AR (Belum Matang)',
    overdue:       'Invois AR (Tertunggak)',
    totalCol:      'Jumlah AR',
    loading:       'Sedang dimuat turun...',
    noData:        'Tiada rekod AR',
    error:         'Gagal dimuat turun',
    invoicePending:'Invois AR (Proses Penghantaran)',
    invoicePredue: 'Invois AR (Belum Matang)',
    invoiceOverdue:'Invois AR (Tertunggak)',
    invoiceId:     'ID Invois',
    amount:        'Jumlah (RM)',
    comment:       'Ulasan',
    update:        'Kemaskini',
    updateTitle:   'Kemaskini Ulasan',
    cancel:        'Batal',
    save:          'Simpan',
    saving:        'Menyimpan...',
    remarkPh:      'Masukkan ulasan...',
    navAR:         'Ringkasan Kredit & Tunggakan',
    navCAS:        'Semakan Permohonan Kredit',
    navMR:         'Penilaian Kredit Bulanan',
    filterOutletId:   'Cari Outlet ID...',
    filterOutletName: 'Cari Nama Outlet...',
    filterVanId:      'Cari Van ID...',
    filterStatus:     'Semua Status',
    filterClear:      'Padam',
    statusLabel:      'Status AR',
    regionLabel:      'Region',
    allRegions:       'Semua Region',
    siteLabel:        'Site',
    allSites:         'Semua Site',
  },
};
// -- Gauge -----------------------------------------------------
function Gauge({ pct, t }) {
  const clamp = Math.min(Math.max(Number(pct)||0, 0), 100);
  const cx=85, cy=78, r=56;
  const toRad = d => d * Math.PI / 180;
  const pctAngle = p => 180 - p * 1.8;
  function arc(p1, p2, rr) {
    const a1=pctAngle(p1), a2=pctAngle(p2);
    const x1=cx+rr*Math.cos(toRad(a1)), y1=cy-rr*Math.sin(toRad(a1));
    const x2=cx+rr*Math.cos(toRad(a2)), y2=cy-rr*Math.sin(toRad(a2));
    return `M${x1},${y1} A${rr},${rr} 0 0 1 ${x2},${y2}`;
  }
  function wedge(p1, p2, rr) {
    const a1=pctAngle(p1), a2=pctAngle(p2);
    const x1=cx+rr*Math.cos(toRad(a1)), y1=cy-rr*Math.sin(toRad(a1));
    const x2=cx+rr*Math.cos(toRad(a2)), y2=cy-rr*Math.sin(toRad(a2));
    return `M${cx},${cy} L${x1},${y1} A${rr},${rr} 0 0 1 ${x2},${y2} Z`;
  }
  function tickPt(p, offset=17) {
    const a=pctAngle(p);
    return { x: cx+(r+offset)*Math.cos(toRad(a)), y: cy-(r+offset)*Math.sin(toRad(a)) };
  }
  const vAngle = pctAngle(clamp);
  const nx = cx+44*Math.cos(toRad(vAngle)), ny = cy-44*Math.sin(toRad(vAngle));
  const color = clamp<=30?'#1e7e34':clamp<=50?'#b06000':'#c8102e';
  const tk0=tickPt(0,20), tk30=tickPt(30,17), tk50=tickPt(50,17), tk100=tickPt(100,20);
  return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center'}}>
      <svg width={200} height={125} viewBox="-15 -8 200 115">
        <path d={wedge(0,30,r)} fill="#1e7e34" opacity={0.13}/>
        <path d={arc(0,100,r)} fill="none" stroke="#e8e8e8" strokeWidth={12} strokeLinecap="round"/>
        <path d={arc(0,30,r)}   fill="none" stroke="#1e7e34" strokeWidth={12} strokeLinecap="round" opacity={0.5}/>
        <path d={arc(30,50,r)}  fill="none" stroke="#b06000" strokeWidth={12} strokeLinecap="round" opacity={0.3}/>
        <path d={arc(50,100,r)} fill="none" stroke="#c8102e" strokeWidth={12} strokeLinecap="round" opacity={0.25}/>
        {clamp>0 && <path d={arc(0,clamp,r)} fill="none" stroke={color} strokeWidth={12} strokeLinecap="round"/>}
        <line x1={cx} y1={cy} x2={nx} y2={ny} stroke="#1a1a2e" strokeWidth={2.5} strokeLinecap="round"/>
        <circle cx={cx} cy={cy} r={4} fill="#1a1a2e"/>
        <text x={tk0.x}   y={tk0.y+3}   textAnchor="end"    fontSize={8} fill="#999">0%</text>
        <text x={tk30.x}  y={tk30.y-2}  textAnchor="middle" fontSize={8} fill="#1e7e34" fontWeight="700">30%</text>
        <text x={tk50.x}  y={tk50.y-3}  textAnchor="middle" fontSize={8} fill="#b06000">50%</text>
        <text x={tk100.x} y={tk100.y+3} textAnchor="start"  fontSize={8} fill="#999">100%</text>
        <text x={cx} y={cy+20} textAnchor="middle" fontSize={15} fontWeight="700" fill={color}>{clamp.toFixed(1)}%</text>
      </svg>
      <div style={{fontSize:10,color:'#888',marginTop:2}}>{t.gaugeLbl}</div>
      <div style={{fontSize:10,color:'#1e7e34',marginTop:2}}>{t.safeZone}</div>
    </div>
  );
}
// -- Comment overlay --------------------------------------------
function CommentOverlay({ invoice, outletId, region, onClose, onSave, userName, t }) {
  const [comment, setComment] = useState('');
  const [saving, setSaving]   = useState(false);
  async function save() {
    if (!comment.trim()) return;
    setSaving(true);
    try {
      const res = await fetch('/api/ar/comment', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ outletId, invoiceId: invoice.invoiceId, comment: comment.trim(), userName, region }),
      });
      const d = await res.json();
      if (d.success) { onSave(invoice.invoiceId, d.newComment); onClose(); return; }
      else alert('Gagal menyimpan: ' + (d.error || 'Unknown error'));
    } catch(e) { alert('Ralat: ' + e.message); }
    setSaving(false);
  }
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:'0 16px' }}>
      <div style={{ background:'#fff', borderRadius:14, padding:'24px 20px', maxWidth:420, width:'100%', boxShadow:'0 8px 32px rgba(0,0,0,0.2)' }}>
        <div style={{ fontSize:14, fontWeight:700, color:'#1a1a2e', marginBottom:4 }}>{t.updateTitle}</div>
        <div style={{ fontSize:11, color:'#888', marginBottom:12, fontFamily:'monospace' }}>{invoice.invoiceId}</div>
        {invoice.comment && <div style={{ background:'#f8f9fa', borderRadius:6, padding:'8px 12px', fontSize:12, color:'#555', marginBottom:12, whiteSpace:'pre-line', lineHeight:1.6 }}>{invoice.comment}</div>}
        <textarea style={{ width:'100%', padding:'10px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:13, resize:'vertical', minHeight:80, boxSizing:'border-box', fontFamily:'inherit' }}
          placeholder={t.remarkPh} value={comment} onChange={e => setComment(e.target.value)} />
        <div style={{ display:'flex', gap:10, marginTop:12 }}>
          <button style={{ flex:1, padding:'10px 0', background:'#e0e0e0', color:'#333', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }} onClick={onClose}>{t.cancel}</button>
          <button style={{ flex:1, padding:'10px 0', background:NAV_BLUE, color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', opacity:!comment.trim()||saving?0.4:1 }}
            disabled={!comment.trim()||saving} onClick={save}>{saving?t.saving:t.save}</button>
        </div>
      </div>
    </div>
  );
}
// -- Invoice group ----------------------------------------------
function InvoiceGroup({ label, invoices, color, t, outletId, region, onUpdateComment, userName }) {
  const [target, setTarget] = useState(null);
  if (!invoices.length) return null;
  return (
    <div style={{ background:'#fff', borderRadius:10, marginBottom:10, overflow:'hidden', boxShadow:'0 1px 3px rgba(0,0,0,0.06)' }}>
      <div style={{ padding:'10px 14px', borderLeft:`4px solid ${color}`, background:color+'12', fontSize:11, fontWeight:700, color, textTransform:'uppercase', letterSpacing:'0.5px' }}>{label}</div>
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
        <thead>
          <tr style={{ background:'#fafafa' }}>
            <th style={{ padding:'8px 14px', textAlign:'left', color:'#888', fontWeight:600, borderBottom:'0.5px solid #eee' }}>{t.invoiceId}</th>
            <th style={{ padding:'8px 14px', textAlign:'right', color:'#888', fontWeight:600, borderBottom:'0.5px solid #eee', whiteSpace:'nowrap' }}>{t.amount}</th>
            <th style={{ padding:'8px 14px', textAlign:'left', color:'#888', fontWeight:600, borderBottom:'0.5px solid #eee' }}>{t.comment}</th>
            <th style={{ padding:'8px 14px', textAlign:'center', color:'#888', fontWeight:600, borderBottom:'0.5px solid #eee', width:60 }}></th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((inv, i) => (
            <tr key={i} style={{ borderBottom:'0.5px solid #f5f5f5' }}>
              <td style={{ padding:'9px 14px', color:'#333', fontFamily:'monospace', fontSize:11 }}>{inv.invoiceId}</td>
              <td style={{ padding:'9px 14px', textAlign:'right', color:'#1a1a2e', fontWeight:600, whiteSpace:'nowrap' }}>{fmtNum(inv.outstanding)}</td>
              <td style={{ padding:'9px 14px', color:'#555', fontSize:11, maxWidth:200, whiteSpace:'pre-wrap', wordBreak:'break-word', lineHeight:1.5 }}>{inv.comment||<span style={{ color:'#ccc' }}>-</span>}</td>
              <td style={{ padding:'9px 14px', textAlign:'center' }}>
                <button style={{ background:'none', border:'1px solid #e0e0e0', borderRadius:6, padding:'4px 10px', cursor:'pointer', fontSize:11, color:'#555' }} onClick={() => setTarget(inv)}>Edit</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {target && <CommentOverlay invoice={target} outletId={outletId} region={region}
        onClose={() => setTarget(null)} onSave={(id,c) => { onUpdateComment(id,c); setTarget(null); }}
        userName={userName} t={t} />}
    </div>
  );
}
// -- Outlet modal -----------------------------------------------
function OutletModal({ outlet, region, onClose, onUpdateComment, userName, t }) {
  const groups = [
    { label:t.invoicePending, key:'No Due Date', color:'#546e7a' },
    { label:t.invoicePredue,  key:'Predue',      color:'#b06000' },
    { label:t.invoiceOverdue, key:'Overdue',     color:'#c8102e' },
  ];
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'flex-end', justifyContent:'center' }}
      onClick={e => e.target===e.currentTarget && onClose()}>
      <div style={{ background:'#f0f2f5', width:'100%', maxWidth:700, maxHeight:'92vh', borderRadius:'16px 16px 0 0', overflow:'hidden', display:'flex', flexDirection:'column' }}>
        <div style={{ background:'#fff', padding:'16px 20px', borderBottom:'1px solid #f0f0f0', display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexShrink:0 }}>
          <div>
            <div style={{ fontSize:15, fontWeight:700, color:'#1a1a2e' }}>{outlet.outletName}</div>
            <div style={{ fontSize:12, color:'#888', marginTop:2 }}>{outlet.outletId}</div>
          </div>
          <button style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#888', padding:0 }} onClick={onClose}>x</button>
        </div>
        <div style={{ overflowY:'auto', padding:'12px 16px', flex:1 }}>
          {groups.map(g => <InvoiceGroup key={g.key} label={g.label} color={g.color}
            invoices={outlet.invoices.filter(inv => inv.arStatus===g.key)}
            t={t} outletId={outlet.outletId} region={region}
            onUpdateComment={(id,c) => onUpdateComment(outlet.outletId,id,c)} userName={userName} />)}
        </div>
      </div>
    </div>
  );
}
// -- Sidebar ----------------------------------------------------
function Sidebar({ open, onClose, navigate, t }) {
  if (!open) return null;
  const items = [
    { label:t.navAR,  path:'/cas/ar',        active:true  },
    { label:t.navCAS, path:'/cas/dashboard',  active:false },
    { label:t.navMR,  path:'/cas/monthly-review', active:false },
  ];
  return (
    <>
      <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:300 }} onClick={onClose} />
      <div style={{ position:'fixed', left:0, top:0, bottom:0, width:240, background:'#fff', zIndex:400, boxShadow:'4px 0 16px rgba(0,0,0,0.15)', display:'flex', flexDirection:'column' }}>
        <div style={{ background:NAV_BLUE, padding:'20px 16px 16px', color:'#fff' }}>
          <div style={{ fontSize:13, fontWeight:700 }}>Ninja Mart Credit</div>
          <div style={{ fontSize:11, opacity:0.7, marginTop:2 }}>Sales Portal</div>
        </div>
        <div style={{ flex:1, padding:'12px 0' }}>
          {items.map(item => (
            <div key={item.path}
              style={{ padding:'12px 20px', cursor:'pointer', fontSize:13, fontWeight:item.active?700:400, color:item.active?NAV_BLUE:'#555', background:item.active?NAV_BLUE+'12':'transparent', borderLeft:item.active?`3px solid ${NAV_BLUE}`:'3px solid transparent' }}
              onClick={() => { navigate(item.path); onClose(); }}>
              {item.label}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
// -- Main ------------------------------------------------------
export default function ARPortfolio() {
  const { user, logout } = useAuth();
  const maint = useMaintenanceCheck('sales');
  const navigate = useNavigate();
  const [lang, setLang]           = useState(() => localStorage.getItem('cas_lang') || 'bm');
  const deferredLang = useDeferredValue(lang);
  const [, startTransition] = useTransition();
  const t = LANG[lang];
  const tDeferred = LANG[deferredLang];
  const [data, setData]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [openOutlet, setOpenOutlet]   = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [fOutletId,   setFOutletId]   = useState('');
  const [fOutletName, setFOutletName] = useState('');
  const [fVanId,      setFVanId]      = useState('');
  const [fStatus,     setFStatus]     = useState('');
  const [fRegion,     setFRegion]     = useState('');
  const [fSites,      setFSites]      = useState([]);
  function toggleLang() { const n=lang==='en'?'bm':'en'; localStorage.setItem('cas_lang',n); startTransition(() => setLang(n)); }
  useEffect(() => {
    if (!profileOpen) return;
    const close = () => setProfileOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [profileOpen]);
  async function load() {
    setLoading(true); setError('');
    try {
      const res = await fetch(`/api/ar/data?region=${encodeURIComponent(arRegionsOf(user).join(','))}`);
      if (!res.ok) throw new Error(await res.text());
      setData(await res.json());
    } catch(e) { setError(t.error); }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);
  function handleUpdateComment(outletId, invoiceId, newComment) {
    setData(prev => prev.map(r => r.outletId===outletId && r.invoiceId===invoiceId ? { ...r, comment:newComment } : r));
  }
  const siteOf = v => { const t = (v || '').trim().toUpperCase(); return /^(NMV|SHV)\d{4}$/.test(t) ? '1A' : t.slice(1, 3); };
  function toggleSite(site) {
    setFSites(prev => prev.includes(site) ? prev.filter(s => s !== site) : [...prev, site]);
  }
  const availableSites = useMemo(() => {
    const set = new Set();
    data.forEach(r => (r.vanId || '').split('|').map(v => v.trim()).filter(Boolean).forEach(v => set.add(siteOf(v))));
    return [...set].filter(Boolean).sort();
  }, [data]);
  const availableRegions = useMemo(() => [...new Set(data.map(r => r.region).filter(Boolean))], [data]);
  // Single source of truth: every filter (Outlet ID/Name, Van ID, Status,
  // Site, Region) applies at the row (invoice) level here. Both the summary
  // cards below and the outlet breakdown (byOutlet) derive from this same
  // filtered set, so selecting e.g. "Overdue" or a Site correctly shrinks
  // both the totals and the outlet list together instead of diverging.
  const rowFiltered = useMemo(() => data.filter(r => {
    if (fOutletId   && !(r.outletId || '').toUpperCase().includes(fOutletId.toUpperCase()))     return false;
    if (fOutletName && !(r.outletName || '').toUpperCase().includes(fOutletName.toUpperCase()))  return false;
    if (fVanId) {
      const vans = (r.vanId || '').split('|').map(v => v.trim()).filter(Boolean);
      if (!vans.some(v => v.toUpperCase().includes(fVanId.toUpperCase()))) return false;
    }
    if (fStatus && r.arStatus !== fStatus) return false;
    if (fRegion && r.region !== fRegion) return false;
    if (fSites.length > 0) {
      const vans = (r.vanId || '').split('|').map(v => v.trim()).filter(Boolean);
      if (!vans.some(v => fSites.includes(siteOf(v)))) return false;
    }
    return true;
  }), [data, fOutletId, fOutletName, fVanId, fStatus, fRegion, fSites]);
  const { totalAR, totalOverdue, pctOverdue, uniqueOutlets } = useMemo(() => {
    const tAR  = rowFiltered.reduce((s,r) => s+(Number(r.outstanding)||0), 0);
    const tOD  = rowFiltered.filter(r => r.arStatus==='Overdue').reduce((s,r) => s+(Number(r.outstanding)||0), 0);
    return {
      totalAR:      tAR,
      totalOverdue: tOD,
      pctOverdue:   tAR>0 ? (tOD/tAR)*100 : 0,
      uniqueOutlets: [...new Set(rowFiltered.map(r => r.outletId))],
    };
  }, [rowFiltered]);
  const byOutlet = useMemo(() => {
    const map = {};
    rowFiltered.forEach(r => {
      if (!map[r.outletId]) map[r.outletId] = { outletId:r.outletId, outletName:r.outletName, vanId:r.vanId, invoices:[] };
      map[r.outletId].invoices.push(r);
    });
    return Object.values(map).map(o => {
      const pending = o.invoices.filter(r=>r.arStatus==='No Due Date').reduce((s,r)=>s+(Number(r.outstanding)||0),0);
      const predue  = o.invoices.filter(r=>r.arStatus==='Predue').reduce((s,r)=>s+(Number(r.outstanding)||0),0);
      const overdue = o.invoices.filter(r=>r.arStatus==='Overdue').reduce((s,r)=>s+(Number(r.outstanding)||0),0);
      return { ...o, pending, predue, overdue, total:pending+predue+overdue };
    }).sort((a,b) => b.overdue-a.overdue);
  }, [rowFiltered]);
  const openOutletData = openOutlet ? byOutlet.find(o=>o.outletId===openOutlet) : null;
  if (maint.blocked) return <MaintenanceScreen />;
  return (
    <div style={{ minHeight:'100vh', background:'#f0f4f8', fontFamily:'system-ui,-apple-system,sans-serif' }}>
      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        @media(max-width:600px){
          .ar-stats-grid{grid-template-columns:1fr 1fr!important;}
          .ar-stats-grid>:last-child{grid-column:1/-1!important;}
          .ar-logo{display:none!important;}
          .ar-page-title{display:none!important;}
          .ar-nav-sub{display:none!important;}
          .ar-filter-grp{flex:1 1 calc(50% - 4px)!important;min-width:130px!important;}
          .ar-filter-grp input,.ar-filter-grp select{width:100%!important;box-sizing:border-box!important;}
        }
      `}</style>
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} navigate={navigate} t={t} />
      <nav style={{ background:NAV_BLUE, padding:'10px 20px', display:'flex', justifyContent:'space-between', alignItems:'center', position:'sticky', top:0, zIndex:100 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button style={{ background:'rgba(255,255,255,0.15)', border:'none', borderRadius:6, padding:'6px 8px', cursor:'pointer', color:'#fff', fontSize:16, lineHeight:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:3 }}
            onClick={() => setSidebarOpen(true)}>
            <span style={{ display:'block', width:18, height:2, background:'#fff', borderRadius:1 }} />
            <span style={{ display:'block', width:18, height:2, background:'#fff', borderRadius:1 }} />
            <span style={{ display:'block', width:18, height:2, background:'#fff', borderRadius:1 }} />
          </button>
          <img
            src="/logo-wordmark.svg"
            alt="Ninja Mart"
            width={26 * 3.84}
            height={26}
            style={{ height: 26, width: 26 * 3.84, objectFit: 'contain' }}
            className="ar-logo"
            onError={e => { e.target.style.display = 'none'; }}
          />
          <div style={{ color:'#fff', fontWeight:700, fontSize:15 }} className="ar-page-title">{t.pageTitle(regionLabel(user))}</div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ position:'relative' }}>
            <div style={{ textAlign:'right', cursor:'pointer', userSelect:'none' }}
              onClick={e => { e.stopPropagation(); setProfileOpen(p=>!p); }}>
              <div style={{ fontWeight:600, color:'#fff', fontSize:13 }}>{user.displayName}<span style={{ display:'inline-block', width:0, height:0, borderLeft:'4px solid transparent', borderRight:'4px solid transparent', borderTop:'5px solid #fff', marginLeft:5, verticalAlign:'middle' }} /></div>
              <div style={{ fontSize:11, color:'rgba(255,255,255,0.7)', marginTop:1 }} className="ar-nav-sub">{getUserTitle(user.email)} - {regionLabel(user)}</div>
            </div>
            {profileOpen && (
              <div style={{ position:'absolute', top:'100%', right:0, marginTop:8, background:'#fff', borderRadius:8, boxShadow:'0 4px 16px rgba(0,0,0,0.15)', minWidth:160, zIndex:200, overflow:'hidden' }}>
                <button style={{ width:'100%', padding:'12px 16px', background:'none', border:'none', textAlign:'left', cursor:'pointer', fontSize:13, color:'#c5221f', fontWeight:600 }}
                  onClick={e => { e.stopPropagation(); logout(); }}>{t.logout}</button>
              </div>
            )}
          </div>
          <button style={{ background:'rgba(255,255,255,0.15)', border:'1px solid rgba(255,255,255,0.3)', color:'#fff', padding:'3px 10px', borderRadius:4, cursor:'pointer', fontSize:12, fontWeight:600 }}
            onClick={toggleLang}>
            <span style={{ opacity:lang==='bm'?1:0.5 }}>BM</span>{' | '}
            <span style={{ opacity:lang==='en'?1:0.5 }}>EN</span>
          </button>
        </div>
      </nav>
      <div style={{ maxWidth:1200, margin:'0 auto', padding:'20px 16px' }}>
        {/* Stats */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1.6fr', gap:12, marginBottom:16 }} className="ar-stats-grid">
          {[
            { val:uniqueOutlets.length, lbl:t.activeOutlets, big:true,  color:NAV_BLUE     },
            { val:fmtNum(totalAR),      lbl:t.totalAR,       big:false, color:'#1a1a2e'    },
            { val:fmtNum(totalOverdue), lbl:t.totalOverdue,  big:false, color:'#c8102e'    },
          ].map((st,i) => (
            <div key={i} style={{ background:'#fff', borderRadius:10, boxShadow:'0 1px 4px rgba(0,0,0,0.07)', padding:'16px 20px', borderTop:`3px solid ${st.color}` }}>
              {st.big
                ? <div style={{ fontSize:36, fontWeight:700, color:st.color }}>{st.val}</div>
                : <div>
                    <div style={{ fontSize:11, color:'#aaa', fontWeight:400 }}>RM</div>
                    <div style={{ fontSize:18, fontWeight:700, color:st.color, lineHeight:1.2 }}>{st.val}</div>
                  </div>
              }
              <div style={{ fontSize:12, color:'#888', marginTop:6 }}>{st.lbl}</div>
            </div>
          ))}
          <div style={{ background:pctOverdue<=30?'#fff':'#fff0f2', borderRadius:10, boxShadow:'0 1px 4px rgba(0,0,0,0.07)', padding:'16px 20px', display:'flex', alignItems:'center', justifyContent:'center', borderTop:`3px solid ${pctOverdue<=30?'#1e7e34':'#c8102e'}` }}>
            <Gauge pct={pctOverdue} t={t} />
          </div>
        </div>
        {/* Filters */}
        <div style={{ background:'#fff', borderRadius:10, boxShadow:'0 1px 4px rgba(0,0,0,0.07)', padding:'12px 18px', marginBottom:12, display:'flex', gap:10, flexWrap:'wrap', alignItems:'flex-end' }}>
          {[
            { label:t.outletId,   val:fOutletId,   set:setFOutletId,   ph:t.filterOutletId   },
            { label:t.outletName, val:fOutletName, set:setFOutletName, ph:t.filterOutletName },
            { label:t.vanId,      val:fVanId,      set:setFVanId,      ph:t.filterVanId      },
          ].map(f => (
            <div key={f.label} style={{ display:'flex', flexDirection:'column', gap:3 }} className="ar-filter-grp">
              <span style={{ fontSize:11, color:'#888', fontWeight:600 }}>{f.label}</span>
              <input style={{ padding:'6px 10px', border:'1px solid #ddd', borderRadius:6, fontSize:13, width:140 }}
                placeholder={f.ph} value={f.val} onChange={e => f.set(e.target.value)} />
            </div>
          ))}
          <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
            <span style={{ fontSize:11, color:'#888', fontWeight:600 }}>{t.statusLabel}</span>
            <select style={{ padding:'6px 10px', border:'1px solid #ddd', borderRadius:6, fontSize:13, background:'#fff' }}
              value={fStatus} onChange={e => setFStatus(e.target.value)}>
              <option value="">{t.filterStatus}</option>
              <option value="No Due Date">{t.pending}</option>
              <option value="Predue">{t.predue}</option>
              <option value="Overdue">{t.overdue}</option>
            </select>
          </div>
          {availableRegions.length > 1 && (
            <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
              <span style={{ fontSize:11, color:'#888', fontWeight:600 }}>{t.regionLabel}</span>
              <select style={{ padding:'6px 10px', border:'1px solid #ddd', borderRadius:6, fontSize:13, background:'#fff' }}
                value={fRegion} onChange={e => setFRegion(e.target.value)}>
                <option value="">{t.allRegions}</option>
                {availableRegions.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          )}
          {availableSites.length > 0 && (
            <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
              <span style={{ fontSize:11, color:'#888', fontWeight:600 }}>{t.siteLabel}</span>
              <div style={{ display:'flex', flexWrap:'wrap', gap:5, maxWidth:280 }}>
                {availableSites.map(site => (
                  <button key={site} type="button" onClick={() => toggleSite(site)} style={{
                    padding:'4px 10px', borderRadius:14, fontSize:12, cursor:'pointer',
                    border: fSites.includes(site) ? '1px solid #1967d2' : '1px solid #ddd',
                    background: fSites.includes(site) ? '#1967d2' : '#fff',
                    color: fSites.includes(site) ? '#fff' : '#555',
                  }}>{site}</button>
                ))}
              </div>
            </div>
          )}
          <button style={{ padding:'6px 14px', background:'#f0f4f8', border:'1px solid #ddd', borderRadius:6, cursor:'pointer', fontSize:12, color:'#555', alignSelf:'flex-end' }}
            onClick={() => { setFOutletId(''); setFOutletName(''); setFVanId(''); setFStatus(''); setFRegion(''); setFSites([]); }}>
            {t.filterClear}
          </button>
        </div>
        {/* Table */}
        <div style={{ background:'#fff', borderRadius:10, boxShadow:'0 1px 4px rgba(0,0,0,0.07)', overflow:'hidden' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 18px', borderBottom:'1px solid #f0f0f0' }}>
            <div style={{ fontSize:14, fontWeight:700, color:'#1a1a2e' }}>{t.outletList}</div>
            <button style={{ background:'#f0f4f8', border:'1px solid #ddd', borderRadius:6, padding:'6px 14px', cursor:'pointer', fontSize:12, color:'#555' }} onClick={load}>{t.refresh}</button>
          </div>
          {loading ? (
            <div style={{ padding:48, textAlign:'center', color:'#888', fontSize:13 }}>
              <div style={{ width:24, height:24, border:'3px solid #eee', borderTopColor:NAV_BLUE, borderRadius:'50%', animation:'spin 0.7s linear infinite', margin:'0 auto 12px' }} />
              {t.loading}
            </div>
          ) : error ? (
            <div style={{ padding:48, textAlign:'center', color:'#c5221f', fontSize:13 }}>{error}</div>
          ) : byOutlet.length===0 ? (
            <div style={{ padding:48, textAlign:'center', color:'#888', fontSize:13 }}>{t.noData}</div>
          ) : (
            <div style={{ overflowX:'auto', overflowY:'auto', maxHeight:'calc(100vh - 380px)' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr>
                    {[
                      { l:tDeferred.outletId,   align:'left'                    },
                      { l:tDeferred.outletName, align:'left',  minWidth:220      },
                      { l:tDeferred.vanId,      align:'left',  minWidth:100      },
                      { l:tDeferred.pending,    align:'right', color:'#546e7a'   },
                      { l:tDeferred.predue,     align:'right', color:'#b06000'   },
                      { l:tDeferred.overdue,    align:'right', color:'#c8102e'   },
                      { l:tDeferred.totalCol,   align:'right'                    },
                    ].map((h,i) => (
                      <th key={i} style={{ padding:'10px 14px', textAlign:h.align, fontSize:11, fontWeight:700, color:h.color||'#555', textTransform:'uppercase', letterSpacing:'0.4px', borderBottom:'2px solid #e0e0e0', background:'#f0f4f8', whiteSpace:'nowrap', position:'sticky', top:0, zIndex:10, minWidth:h.minWidth||'auto' }}>{h.l}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {byOutlet.map(o => (
                    <tr key={o.outletId}
                      style={{ cursor:'pointer', borderLeft:o.overdue>0?'3px solid #c8102e':'3px solid #e0f0ff' }}
                      onClick={() => setOpenOutlet(o.outletId)}
                      onMouseEnter={e => e.currentTarget.style.background='#f0f4f8'}
                      onMouseLeave={e => e.currentTarget.style.background=''}>
                      <td style={{ padding:'12px 14px', fontSize:13, borderBottom:'1px solid #f0f0f0' }}>{o.outletId}</td>
                      <td style={{ padding:'12px 14px', fontSize:13, borderBottom:'1px solid #f0f0f0', minWidth:220 }}>{o.outletName}</td>
                      <td style={{ padding:'12px 14px', fontSize:12, borderBottom:'1px solid #f0f0f0', color:'#888', minWidth:100 }}>
                        {[...new Set(o.invoices.flatMap(inv=>(inv.vanId||'').split('|').map(v=>v.trim()).filter(Boolean)))].map((v,i)=><div key={i}>{v}</div>)}
                      </td>
                      <td style={{ padding:'12px 14px', fontSize:13, borderBottom:'1px solid #f0f0f0', textAlign:'right', color:'#546e7a' }}>{o.pending>0?fmtNum(o.pending):'-'}</td>
                      <td style={{ padding:'12px 14px', fontSize:13, borderBottom:'1px solid #f0f0f0', textAlign:'right', color:'#b06000' }}>{o.predue>0?fmtNum(o.predue):'-'}</td>
                      <td style={{ padding:'12px 14px', fontSize:13, borderBottom:'1px solid #f0f0f0', textAlign:'right', color:o.overdue>0?'#c8102e':'#aaa', fontWeight:o.overdue>0?700:400 }}>{o.overdue>0?fmtNum(o.overdue):'-'}</td>
                      <td style={{ padding:'12px 14px', fontSize:13, borderBottom:'1px solid #f0f0f0', textAlign:'right', fontWeight:600 }}>{fmtNum(o.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {openOutletData && (
        <OutletModal outlet={openOutletData} region={arRegionsOf(user).join(',')}
          onClose={() => setOpenOutlet(null)}
          onUpdateComment={handleUpdateComment}
          userName={user.displayName} t={t} />
      )}
    </div>
  );
}